import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { FileText, ArrowLeft } from '@phosphor-icons/react';

export default function Terms() {
  return (
    <div className="min-h-screen py-12">
      <SEO
        title="Terms of Service"
        description="Grade9Ready terms of service. Read our terms covering account responsibility, acceptable use, payments, subscriptions, intellectual property, and safeguarding for our GCSE revision platform."
        path="/terms"
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-8">
          <ArrowLeft size={16} /> Back to Home
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-amber-500 rounded-xl border-2 border-foreground flex items-center justify-center shadow-[3px_3px_0px_0px_rgb(var(--9ready-shadow))]">
            <FileText size={24} weight="fill" className="text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-heading font-bold">Terms of Service</h1>
            <p className="text-muted-foreground text-sm">Grade9Ready Terms of Service</p>
          </div>
        </div>

        <div className="prose prose-sm max-w-none space-y-8" data-testid="terms-content">
          <section className="neo-card p-6">
            <h2 className="text-xl font-bold mb-3">1. Introduction</h2>
            <p className="text-muted-foreground leading-relaxed">
              These Terms of Service ("Terms") govern your use of Grade9Ready ("we", "us", "our", "the platform").
            </p>
            <p className="text-muted-foreground leading-relaxed mt-2">
              By accessing or using Grade9Ready, you agree to these Terms. If you do not agree, you must not use the platform.
            </p>
          </section>

          <section className="neo-card p-6">
            <h2 className="text-xl font-bold mb-3">2. Eligibility</h2>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground mb-3">
              <li>Students under 18 may use the platform</li>
              <li>Parents/guardians may create linked accounts</li>
            </ul>
            <p className="text-muted-foreground mb-2">By using the platform, you confirm:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>You are legally able to enter into these Terms, or</li>
              <li>You have parental/guardian consent</li>
            </ul>
          </section>

          <section className="neo-card p-6">
            <h2 className="text-xl font-bold mb-3">3. Accounts</h2>

            <h3 className="text-base font-bold mt-2 mb-2">3.1 Account Responsibility</h3>
            <p className="text-muted-foreground mb-2">You are responsible for:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground mb-2">
              <li>Keeping login details secure</li>
              <li>All activity under your account</li>
            </ul>
            <p className="text-muted-foreground mb-4">We are not liable for unauthorised access resulting from user negligence.</p>

            <h3 className="text-base font-bold mt-2 mb-2">3.2 Parent-Student Linking</h3>
            <p className="text-muted-foreground mb-2">Parent accounts may only be linked using:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground mb-3">
              <li>The student's email address</li>
              <li>A valid pairing code</li>
            </ul>
            <p className="text-muted-foreground mb-2">You must not attempt to:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground mb-2">
              <li>Access accounts without authorisation</li>
              <li>Circumvent the pairing system</li>
            </ul>
            <p className="text-muted-foreground">We reserve the right to suspend accounts where misuse is suspected.</p>
          </section>

          <section className="neo-card p-6">
            <h2 className="text-xl font-bold mb-3">4. Acceptable Use</h2>
            <p className="text-muted-foreground mb-2">You agree not to:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground mb-3">
              <li>Use the platform for unlawful purposes</li>
              <li>Attempt to gain unauthorised access to accounts or systems</li>
              <li>Share, copy, or redistribute course content</li>
              <li>Upload harmful, abusive, or inappropriate material</li>
              <li>Interfere with platform functionality</li>
            </ul>
            <p className="text-muted-foreground">We may suspend or terminate accounts for breaches.</p>

            <h3 className="text-base font-bold mt-4 mb-2">4.1 Achievement Sharing</h3>
            <p className="text-muted-foreground">Students may generate achievement cards showing their first name and general progress statistics (XP, level, streak, expected grade). These cards may be downloaded and shared externally at the student's discretion. By using this feature, you acknowledge that any information shared outside the platform is your responsibility. Achievement cards do not contain email addresses, passwords, or other sensitive account information.</p>
          </section>

          <section className="neo-card p-6">
            <h2 className="text-xl font-bold mb-3">5. Services</h2>
            <p className="text-muted-foreground mb-2">Grade9Ready provides:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground mb-3">
              <li>Educational content and tools</li>
              <li>Progress tracking</li>
              <li>Parent-linked monitoring features</li>
            </ul>
            <p className="text-muted-foreground mb-2">We do not guarantee:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>Exam results</li>
              <li>Academic outcomes</li>
              <li>Specific grades</li>
            </ul>
          </section>

          <section className="neo-card p-6">
            <h2 className="text-xl font-bold mb-3">6. Payments and Subscriptions</h2>

            <h3 className="text-base font-bold mt-2 mb-1">6.1 Pricing</h3>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground mb-3">
              <li>Prices are displayed at the point of purchase</li>
              <li>We reserve the right to update pricing at any time</li>
            </ul>

            <h3 className="text-base font-bold mt-2 mb-1">6.2 Subscriptions (if applicable)</h3>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground mb-3">
              <li>Subscriptions renew automatically unless cancelled</li>
              <li>You are responsible for cancelling before renewal</li>
            </ul>

            <h3 className="text-base font-bold mt-2 mb-1">6.3 Refunds</h3>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>Digital products are generally non-refundable once accessed, unless required by law</li>
              <li>Refunds may be considered at our discretion in exceptional circumstances</li>
            </ul>
          </section>

          <section className="neo-card p-6">
            <h2 className="text-xl font-bold mb-3">7. Intellectual Property</h2>
            <p className="text-muted-foreground mb-2">All content on Grade9Ready, including courses, materials, and branding, is owned by or licensed to us.</p>
            <p className="text-muted-foreground mb-2">You may not:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>Copy, distribute, or resell content</li>
              <li>Use content for commercial purposes</li>
            </ul>
          </section>

          <section className="neo-card p-6">
            <h2 className="text-xl font-bold mb-3">8. Suspension and Termination</h2>
            <p className="text-muted-foreground mb-2">We may suspend or terminate accounts if:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground mb-2">
              <li>These Terms are breached</li>
              <li>Misuse or suspicious activity is detected</li>
              <li>Required for legal or security reasons</li>
            </ul>
            <p className="text-muted-foreground">We are not liable for losses resulting from termination due to breaches.</p>
          </section>

          <section className="neo-card p-6">
            <h2 className="text-xl font-bold mb-3">9. Limitation of Liability</h2>
            <p className="text-muted-foreground mb-2">To the fullest extent permitted by law, Grade9Ready is not liable for:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground mb-3">
              <li>Loss of academic performance</li>
              <li>Indirect or consequential losses</li>
              <li>Loss of data caused by user actions</li>
            </ul>
            <p className="text-muted-foreground mb-2">Nothing in these Terms excludes liability for:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>Death or personal injury caused by negligence</li>
              <li>Fraud or fraudulent misrepresentation</li>
            </ul>
          </section>

          <section className="neo-card p-6">
            <h2 className="text-xl font-bold mb-3">10. Availability of Service</h2>
            <p className="text-muted-foreground mb-2">We aim to provide continuous access but do not guarantee:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground mb-3">
              <li>Uninterrupted service</li>
              <li>Error-free operation</li>
            </ul>
            <p className="text-muted-foreground mb-2">We may:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>Update, modify, or suspend parts of the platform</li>
              <li>Perform maintenance without notice</li>
            </ul>
          </section>

          <section className="neo-card p-6">
            <h2 className="text-xl font-bold mb-3">11. Data Protection</h2>
            <p className="text-muted-foreground">Your use of the platform is also governed by our <Link to="/privacy" className="text-primary hover:underline font-bold">Privacy Policy</Link>.</p>
          </section>

          <section className="neo-card p-6">
            <h2 className="text-xl font-bold mb-3">12. Safeguarding</h2>
            <p className="text-muted-foreground mb-2">We are committed to providing a safe platform for young users. Users must:</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
              <li>Respect safeguarding measures</li>
              <li>Not attempt to bypass security systems (including pairing codes)</li>
            </ul>
          </section>

          <section className="neo-card p-6">
            <h2 className="text-xl font-bold mb-3">13. Changes to Terms</h2>
            <p className="text-muted-foreground">We may update these Terms from time to time. Continued use of the platform constitutes acceptance of updated Terms.</p>
          </section>

          <section className="neo-card p-6">
            <h2 className="text-xl font-bold mb-3">14. Governing Law</h2>
            <p className="text-muted-foreground">These Terms are governed by the laws of the United Kingdom. Any disputes will be subject to the exclusive jurisdiction of UK courts.</p>
          </section>

          <section className="neo-card p-6">
            <h2 className="text-xl font-bold mb-3">15. Contact</h2>
            <p className="text-muted-foreground mb-2">For any questions regarding these Terms:</p>
            <p className="font-bold"><a href="mailto:datacontroller@grade9ready.com" className="text-primary hover:underline">datacontroller@grade9ready.com</a></p>
          </section>
        </div>
      </div>
    </div>
  );
}
